/**
 * 剪贴板历史抽屉。
 *
 * 布局原则：
 *
 *   - **左右双栏**：左 = PC 来源（host / manual，sky 色调），右 = 设备
 *     来源（device，emerald 色调）。两栏底色不同，视觉上一眼分开。
 *   - 严格按方向区分主动作：左栏「→ 复制到手机」（写设备剪贴板 +
 *     paste 到当前 focus，"复制+自动粘贴"语义），右栏「← 复制到电脑」
 *     （写主机剪贴板，写成功立即把这条 push 进 PC 历史顶部并 active，
 *     不等 host poll 1.5s tick）。
 *   - 每栏的最新一条加「当前 PC / 手机 剪贴板」徽标 + 加重 ring，让
 *     用户一眼看出当前两端剪贴板里各放着什么内容。
 *   - 顶部 serial 显示 + 搜索 + 新建条目；底部不再有 Quick Actions
 *     —— 任意条目自身的主动作就是"复制"，已经覆盖了原 strip 的作用。
 *
 * 视觉与交互：
 *
 *   - 用 portal 到 `document.body`，但 **位置跟随 FloatingMirror 浮窗**
 *     的 pos / size 计算 —— 默认贴在镜像右侧 8 px gap；右侧空间不够时
 *     翻到镜像左侧；全屏 / minimized 由 mirror state 主导兜底位置或
 *     直接隐藏。这样视觉上抽屉永远是镜像的"附属面板"。
 *   - 进出动画：phase = closed | entering | open | exiting；entering/
 *     exiting 期间走 scale + opacity transition，transform-origin 朝向
 *     镜像（右弹左、左弹右），效果是从工具栏剪贴板按钮处展开 / 缩回。
 *   - 颜色：抽屉用 bg-bg-soft + accent (cyan) 边框，与 mirror 浮窗的
 *     bg-bg-card + violet 边形成清晰的两层视觉。
 *
 * 数据：
 *
 *   - 历史读写都走 `useClipboardHistory / useClipboardActions`，最终落到
 *     用户级 prefs (`prefs.devices.json` 的 `<serial>.clipboard`)。
 *   - 编辑 / 删除 / 置顶都是当前 serial 桶内操作，不会影响其他设备。
 */
import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import {
  Bookmark,
  BookmarkPlus,
  Check,
  Copy,
  Monitor,
  Pencil,
  Plus,
  Search,
  Smartphone,
  Trash2,
  X,
} from 'lucide-react';
import { useMirror } from '@/store/mirror';
import {
  useClipboardActions,
  useClipboardHistory,
  type ClipboardEntry,
} from '@/lib/clipboard/store';

const PANEL_W = 380;
const PANEL_GAP = 8;
const FLASH_MS = 1400;
const ANIM_MS = 200;

export interface ClipboardHistoryPanelProps {
  /** 当前镜像绑定的 serial。null/空表示未挂任何镜像，仅做空态展示。 */
  serial: string | null;
  open: boolean;
  onClose: () => void;
  /**
   * 把文本「复制到手机」—— 写设备剪贴板**并**触发 paste 到当前 focus。
   * 卡片主动作和 Composer 的"保存并复制到手机"共用这一条。语义对齐
   * 用户在设备上看到的"复制+粘贴"一气呵成的体验：用户点了之后立即
   * 在手机当前输入框看到文本出现。
   */
  onPushTextToDevice: ((text: string) => void) | null;
}

type ColumnAccent = 'sky' | 'emerald';
/**
 * `pushed` and `copied` are both success states for their respective
 * directions (PC → device / device → PC). `error` is reserved for
 * navigator.clipboard.writeText failures — Chrome rejects writeText
 * with NotAllowedError when the document is unfocused, and we'd rather
 * surface that loudly than silently no-op.
 */
type FlashKind = 'pushed' | 'copied' | 'error';
type FlashState = { id: string; kind: FlashKind } | null;
type MainActionStatus = 'success' | 'error' | null;
type Phase = 'closed' | 'entering' | 'open' | 'exiting';
type AnchorOrigin = 'top left' | 'top right';

/**
 * Write `text` to the host system clipboard, with a textarea +
 * execCommand fallback for the case Chrome rejects the modern API.
 *
 * Why this exists: `navigator.clipboard.writeText` throws `NotAllowedError`
 * whenever the page isn't the focused document (Chrome's tab-focus gate
 * for clipboard writes). Inside our SPA the user can defocus the tab
 * just by clicking around outside it; clicking back into the panel
 * doesn't always restore focus reliably (the panel is portal'd, and
 * the click target is a button, not the document). Result: ~70% of
 * "← 复制到电脑" clicks in casual use end up silently failing.
 *
 * Strategy:
 *   1. Force-focus the window (no-op if already focused, but flips
 *      Chrome's gate when we just regained focus by clicking the btn).
 *   2. Try the modern `clipboard.writeText`; resolves on success.
 *   3. On reject (or if `clipboard` isn't available — older browsers,
 *      `chrome://` schemes, etc.), fall back to a hidden `<textarea>`
 *      + `document.execCommand('copy')`. execCommand is deprecated
 *      but still works in every shipping browser as a fallback path.
 */
async function writeHostClipboardSafely(text: string): Promise<boolean> {
  if (!text) return false;
  if (typeof window !== 'undefined') {
    try {
      window.focus();
    } catch {
      /* swallow — focus() doesn't throw in well-formed envs but headless / iframes occasionally do */
    }
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to execCommand */
    }
  }
  if (typeof document === 'undefined') return false;
  // Hidden textarea trick — `select()` + `execCommand('copy')` works
  // even when the document isn't strictly "active" in Chrome's eyes,
  // because the focus is moved to a real DOM element we just created.
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  // `position: fixed; opacity: 0` keeps it out of the layout flow and
  // invisible while still being focusable. iOS quirk: needs `font-size
  // >= 16px` to avoid auto-zoom; we don't set font-size here because
  // the field never displays.
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  ta.style.top = '0';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  let ok = false;
  try {
    ta.focus();
    ta.select();
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  } finally {
    document.body.removeChild(ta);
  }
  return ok;
}

/**
 * Track open/close transition phase. Stays mounted during exit so the
 * scale + opacity tween can play out to completion before unmount.
 */
function usePanelPhase(open: boolean): {
  phase: Phase;
  onTransitionEnd: () => void;
} {
  const [phase, setPhase] = useState<Phase>('closed');

  useEffect(() => {
    if (open) {
      setPhase((p) => (p === 'open' ? p : 'entering'));
      const raf = requestAnimationFrame(() => {
        setPhase((p) => (p === 'entering' ? 'open' : p));
      });
      return () => cancelAnimationFrame(raf);
    }
    setPhase((p) => (p === 'closed' || p === 'exiting' ? p : 'exiting'));
    return undefined;
  }, [open]);

  // Failsafe: some browsers occasionally swallow `transitionend` (e.g. when
  // the panel is removed mid-transition by an upstream re-render). A timeout
  // matched to ANIM_MS guarantees we don't get stuck in 'exiting' forever.
  useEffect(() => {
    if (phase !== 'exiting') return;
    const id = window.setTimeout(() => {
      setPhase((p) => (p === 'exiting' ? 'closed' : p));
    }, ANIM_MS + 50);
    return () => window.clearTimeout(id);
  }, [phase]);

  const onTransitionEnd = (): void => {
    setPhase((p) => (p === 'exiting' ? 'closed' : p));
  };

  return { phase, onTransitionEnd };
}

/**
 * Decide where to anchor the panel relative to the floating mirror.
 *
 * Default: stick to the mirror's right edge with a small gap. Flip to
 * the left edge if there isn't enough horizontal room. Fullscreen
 * mirror falls back to the viewport's right edge.
 */
function useAnchor(): {
  style: React.CSSProperties;
  origin: AnchorOrigin;
  hidden: boolean;
} {
  const pos = useMirror((s) => s.pos);
  const size = useMirror((s) => s.size);
  const minimized = useMirror((s) => s.minimized);
  const isFullscreen = useMirror((s) => s.isFullscreen);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onResize = (): void => setTick((t) => t + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return useMemo(() => {
    void tick;
    if (minimized) {
      return { style: { left: -99999, top: 0 }, origin: 'top left', hidden: true };
    }
    const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
    const panelMaxH = Math.round(viewportH * 0.82);

    let left: number;
    let top: number;
    let origin: AnchorOrigin;

    if (isFullscreen) {
      left = viewportW - PANEL_W - 24;
      top = 56;
      origin = 'top right';
    } else {
      const rightEdge = pos.x + size.w;
      const rightSpace = viewportW - rightEdge;
      const leftSpace = pos.x;
      const fitsRight = rightSpace >= PANEL_W + PANEL_GAP + 16;
      const fitsLeft = leftSpace >= PANEL_W + PANEL_GAP + 16;

      if (fitsRight) {
        left = rightEdge + PANEL_GAP;
        origin = 'top left';
      } else if (fitsLeft) {
        left = pos.x - PANEL_W - PANEL_GAP;
        origin = 'top right';
      } else {
        left = Math.max(16, viewportW - PANEL_W - 16);
        origin = 'top right';
      }
      top = pos.y;
    }

    top = Math.max(16, Math.min(viewportH - 120, top));

    return {
      style: {
        left,
        top,
        width: PANEL_W,
        maxHeight: panelMaxH,
      },
      origin,
      hidden: false,
    };
  }, [pos.x, pos.y, size.w, size.h, minimized, isFullscreen, tick]);
}

export function ClipboardHistoryPanel(
  props: ClipboardHistoryPanelProps,
): JSX.Element | null {
  return <ClipboardHistoryPanelImpl {...props} />;
}

function ClipboardHistoryPanelImpl({
  serial,
  open,
  onClose,
  onPushTextToDevice,
}: ClipboardHistoryPanelProps): JSX.Element | null {
  const { phase, onTransitionEnd } = usePanelPhase(open);
  const anchor = useAnchor();
  const entries = useClipboardHistory(serial);
  const actions = useClipboardActions(serial);
  const [search, setSearch] = useState('');
  const [composing, setComposing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [flash, setFlash] = useState<FlashState>(null);

  // ESC 关闭抽屉。Mirror 自己在全屏态下也注册了 ESC，但我们的 listener
  // 在抽屉打开时优先生效；加 stopPropagation 避免误触镜像退出全屏。
  useEffect(() => {
    if (phase === 'closed') return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (editingId != null || composing) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [phase, editingId, composing, onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => e.text.toLowerCase().includes(q));
  }, [entries, search]);

  // host / manual 都进左栏；device 进右栏。
  const hostList = useMemo(
    () => filtered.filter((e) => e.source !== 'device'),
    [filtered],
  );
  const deviceList = useMemo(
    () => filtered.filter((e) => e.source === 'device'),
    [filtered],
  );

  const flashRow = (id: string, kind: FlashKind): void => {
    setFlash({ id, kind });
    window.setTimeout(() => {
      setFlash((cur) => (cur && cur.id === id && cur.kind === kind ? null : cur));
    }, FLASH_MS);
  };

  const onCopyToHost = async (entry: ClipboardEntry): Promise<void> => {
    const ok = await writeHostClipboardSafely(entry.text);
    flashRow(entry.id, ok ? 'copied' : 'error');
    // On success, immediately echo the text into the host history so it
    // shows up at the top of the PC column with the "当前 PC 剪贴板"
    // active badge — same semantic as if the user had Cmd+C'd it. We
    // don't wait for `useHostClipboardPoll` (1.5s tick + visibility
    // guard + dedup) because:
    //   1. The user just clicked, so they expect instant feedback.
    //   2. The host poll is gated by tab-visibility & focus and may
    //      simply never fire if focus drifts elsewhere.
    //   3. Dedup by (text, source) on the actions side means re-adding
    //      the same string is idempotent — the poll, if it later does
    //      fire, won't double-add this entry.
    if (ok && serial) {
      actions.push({ text: entry.text, source: 'host' });
    }
  };

  const onCopyToDevice = (entry: ClipboardEntry): void => {
    if (!onPushTextToDevice) return;
    // "复制到手机" is now a copy-AND-paste op (paste=true): the device
    // clipboard gets the text AND the current focus gets the paste,
    // so the user sees the text appear in the input box on the phone
    // without an extra long-press-paste step. Earlier this was
    // paste=false, but that left users wondering why "复制到手机"
    // didn't actually put the text on screen.
    onPushTextToDevice(entry.text);
    flashRow(entry.id, 'pushed');
  };

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!portalTarget) return null;
  if (phase === 'closed') return null;

  const isOut = phase === 'entering' || phase === 'exiting';
  const animClass = isOut ? 'opacity-0 scale-[0.92]' : 'opacity-100 scale-100';

  const wrapperStyle: React.CSSProperties = {
    ...anchor.style,
    transformOrigin: anchor.origin,
    transitionProperty: 'opacity, transform',
    transitionDuration: `${ANIM_MS}ms`,
    transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
    pointerEvents: anchor.hidden || phase === 'exiting' ? 'none' : undefined,
    visibility: anchor.hidden ? 'hidden' : undefined,
  };

  return createPortal(
    <div
      className={clsx(
        'fixed z-[110] flex flex-col overflow-hidden rounded-lg',
        'bg-bg-soft text-xs text-ink',
        'border border-accent/30 ring-1 ring-accent/15',
        'shadow-[0_24px_56px_rgba(0,0,0,0.55),0_2px_8px_rgba(0,0,0,0.35)]',
        animClass,
      )}
      style={wrapperStyle}
      role="dialog"
      aria-label="剪贴板历史"
      onPointerDown={(e) => e.stopPropagation()}
      onTransitionEnd={onTransitionEnd}
    >
      {/* ── Header ──────────────────────────────────────────── */}
      <div
        className={clsx(
          'flex shrink-0 items-center gap-2 border-b border-accent/25 px-2.5 py-2',
          'bg-accent/10 backdrop-blur-sm',
        )}
      >
        <Bookmark className="h-3.5 w-3.5 text-accent" />
        <span className="font-medium text-ink">剪贴板</span>
        <span
          className="ml-1 max-w-[120px] truncate font-mono text-[10px] text-ink-muted"
          title={serial ?? '未挂任何镜像'}
        >
          {serial ?? '— 未挂镜像 —'}
        </span>
        <span className="ml-auto text-[10px] text-ink-muted">
          {entries.length} 条
        </span>
        <button
          type="button"
          className="rounded p-0.5 text-ink-muted hover:bg-bg hover:text-ink"
          onClick={onClose}
          title="关闭（缩回到剪贴板按钮）"
          aria-label="关闭剪贴板抽屉"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* ── Search + Compose ─────────────────────────────────── */}
      <div className="flex shrink-0 items-center gap-1 border-b border-line/50 px-2 py-1.5">
        <Search className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
        <input
          className="h-7 min-w-0 flex-1 rounded border border-line bg-bg-card px-1.5 text-[11px] text-ink outline-none focus:border-accent"
          placeholder="搜索历史…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          className={clsx(
            'inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-1 text-[10px]',
            composing
              ? 'border-accent/70 bg-accent/15 text-accent'
              : 'border-line text-ink hover:bg-bg-card',
          )}
          onClick={() => setComposing((v) => !v)}
          title={composing ? '收起新建条目' : '新建条目'}
        >
          <Plus className="h-3 w-3" /> 新建
        </button>
      </div>

      {composing && (
        <Composer
          disabled={!serial}
          canPushToDevice={!!onPushTextToDevice}
          onSaveAsPc={(text) => {
            actions.push({ text, source: 'manual' });
            setComposing(false);
          }}
          onSaveAndPush={(text) => {
            actions.push({ text, source: 'manual' });
            onPushTextToDevice?.(text);
            setComposing(false);
          }}
          onCancel={() => setComposing(false)}
        />
      )}

      {/* ── Two columns ────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <Column
          icon={Monitor}
          title="电脑"
          subtitle="PC"
          accent="sky"
          count={hostList.length}
          entries={hostList}
          editingId={editingId}
          setEditingId={setEditingId}
          actions={actions}
          mainActionLabel="→ 复制到手机"
          mainActionFlashLabel="✓ 已复制到手机"
          mainActionErrorLabel="复制失败，重试"
          mainActionDisabled={!onPushTextToDevice}
          mainActionDisabledReason={
            onPushTextToDevice ? '' : '镜像未连接，无法复制到设备'
          }
          onMainAction={onCopyToDevice}
          onCopy={onCopyToHost}
          flash={flash}
          mainActionKind="pushed"
          activeBadgeLabel="当前 PC 剪贴板"
          emptyHint={
            serial
              ? '在 SPA 任意位置 Cmd/Ctrl + C 一段文字 → 这里会自动多一条'
              : '挂载一台镜像后，主机端复制就会写入到该设备的历史'
          }
        />
        <div className="w-px bg-accent/20" />
        <Column
          icon={Smartphone}
          title="手机"
          subtitle="device"
          accent="emerald"
          count={deviceList.length}
          entries={deviceList}
          editingId={editingId}
          setEditingId={setEditingId}
          actions={actions}
          mainActionLabel="← 复制到电脑"
          mainActionFlashLabel="✓ 已复制到电脑"
          mainActionErrorLabel="复制失败，请点窗口聚焦后重试"
          mainActionDisabled={false}
          mainActionDisabledReason=""
          onMainAction={onCopyToHost}
          onCopy={onCopyToHost}
          flash={flash}
          mainActionKind="copied"
          activeBadgeLabel="当前手机剪贴板"
          emptyHint={
            serial
              ? '在手机上长按文字「复制」→ 设备剪贴板会自动同步到这里'
              : '挂载一台镜像后，设备端复制会同步到这里'
          }
        />
      </div>
    </div>,
    portalTarget,
  );
}

/* ===================================================================== */
/* Composer                                                               */
/* ===================================================================== */

function Composer({
  disabled,
  canPushToDevice,
  onSaveAsPc,
  onSaveAndPush,
  onCancel,
}: {
  disabled: boolean;
  canPushToDevice: boolean;
  onSaveAsPc: (text: string) => void;
  onSaveAndPush: (text: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div className="shrink-0 border-b border-line/50 bg-bg/40 p-2">
      <textarea
        ref={ref}
        className="h-20 w-full resize-none rounded border border-line bg-bg-card p-1.5 text-[11px] text-ink outline-none focus:border-accent"
        placeholder="写一段文字，可保存到 PC 历史或直接粘贴到手机…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
        disabled={disabled}
      />
      <div className="mt-1 flex items-center justify-end gap-1">
        <button
          type="button"
          className="rounded border border-line px-2 py-1 text-[10px] text-ink-muted hover:bg-bg-card"
          onClick={onCancel}
        >
          取消
        </button>
        <button
          type="button"
          className="rounded border border-line px-2 py-1 text-[10px] text-ink hover:bg-bg-card disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => text.trim() && onSaveAsPc(text)}
          disabled={disabled || !text.trim()}
          title="保存到 PC 历史，不推送"
        >
          保存为 PC 历史
        </button>
        <button
          type="button"
          className="rounded border border-accent/70 bg-accent/15 px-2 py-1 text-[10px] text-accent hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => text.trim() && onSaveAndPush(text)}
          disabled={disabled || !canPushToDevice || !text.trim()}
          title={
            canPushToDevice
              ? '保存并复制到手机（同时粘贴到当前 focus）'
              : '镜像未连接，无法粘贴'
          }
        >
          保存并复制到手机
        </button>
      </div>
    </div>
  );
}

/* ===================================================================== */
/* Column                                                                 */
/* ===================================================================== */

interface ColumnProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  accent: ColumnAccent;
  count: number;
  entries: ClipboardEntry[];
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  actions: ReturnType<typeof useClipboardActions>;
  mainActionLabel: string;
  mainActionFlashLabel: string;
  /** Label rendered on the main action when the action threw — e.g.
   *  navigator.clipboard.writeText rejected because the SPA tab was
   *  unfocused. Shown for FLASH_MS then reverts. */
  mainActionErrorLabel: string;
  mainActionDisabled: boolean;
  mainActionDisabledReason: string;
  onMainAction: (entry: ClipboardEntry) => void | Promise<void>;
  onCopy: (entry: ClipboardEntry) => void | Promise<void>;
  flash: FlashState;
  /** Which flash kind this column's main-action emits (pushed for left, copied for right). */
  mainActionKind: 'pushed' | 'copied';
  /** Label shown on the most-recent card to flag it as the active system clipboard. */
  activeBadgeLabel: string;
  emptyHint: string;
}

interface AccentClasses {
  /** Column body background tint — both columns share the panel bg, this layers on top. */
  bodyBg: string;
  /** Column header band background. */
  headerBg: string;
  /** Card ring colors (default + hover). */
  cardRing: string;
  cardHoverRing: string;
  /** Active card (most-recent entry) ring + badge color. */
  activeRing: string;
  activeText: string;
  activeDot: string;
  /** Title icon color in the column header. */
  iconColor: string;
  /**
   * Main-action button colors (idle state). Matches the column's hue so
   * the button reads as "this column's primary action" — left column's
   * "→ 复制到手机" is sky, right column's "← 复制到电脑" is emerald.
   * Earlier the button used the panel's accent (cyan) which clashed
   * with the column tint and made each column look like it belonged
   * to a different theme.
   */
  mainBtnIdle: string;
}

const ACCENTS: Record<ColumnAccent, AccentClasses> = {
  sky: {
    bodyBg: 'bg-sky-500/[0.04]',
    headerBg: 'bg-sky-500/10 border-sky-500/20',
    cardRing: 'ring-sky-500/20',
    cardHoverRing: 'hover:ring-sky-500/40',
    activeRing: 'ring-sky-400/60',
    activeText: 'text-sky-300',
    activeDot: 'bg-sky-400',
    iconColor: 'text-sky-400',
    mainBtnIdle:
      'border border-sky-500/60 bg-sky-500/15 text-sky-300 hover:bg-sky-500/25',
  },
  emerald: {
    bodyBg: 'bg-emerald-500/[0.04]',
    headerBg: 'bg-emerald-500/10 border-emerald-500/20',
    cardRing: 'ring-emerald-500/20',
    cardHoverRing: 'hover:ring-emerald-500/40',
    activeRing: 'ring-emerald-400/60',
    activeText: 'text-emerald-300',
    activeDot: 'bg-emerald-400',
    iconColor: 'text-emerald-400',
    mainBtnIdle:
      'border border-emerald-500/60 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25',
  },
};

function Column({
  icon: Icon,
  title,
  subtitle,
  accent,
  count,
  entries,
  editingId,
  setEditingId,
  actions,
  mainActionLabel,
  mainActionFlashLabel,
  mainActionErrorLabel,
  mainActionDisabled,
  mainActionDisabledReason,
  onMainAction,
  onCopy,
  flash,
  mainActionKind,
  activeBadgeLabel,
  emptyHint,
}: ColumnProps): JSX.Element {
  const c = ACCENTS[accent];
  return (
    <div className={clsx('flex min-w-0 flex-1 flex-col', c.bodyBg)}>
      <div
        className={clsx(
          'flex shrink-0 items-center gap-1 border-b px-2 py-1',
          c.headerBg,
        )}
      >
        <Icon className={clsx('h-3.5 w-3.5', c.iconColor)} />
        <span className="text-[11px] font-medium">{title}</span>
        <span className="text-[9px] uppercase text-ink-muted">{subtitle}</span>
        <span className="ml-auto text-[10px] text-ink-muted">{count}</span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="px-2 py-6 text-[10px] leading-relaxed text-ink-muted">
            {emptyHint}
          </div>
        ) : (
          <ul className="space-y-1.5 p-1.5">
            {entries.map((entry, idx) => {
              // Main-action status: success when this row's flash kind
              // matches the column's success kind; error when an attempt
              // failed (only the host-write path can produce 'error');
              // null otherwise. The right column's main action IS the
              // host-write, so it inherits both 'copied' and 'error'.
              let mainStatus: MainActionStatus = null;
              if (flash && flash.id === entry.id) {
                if (flash.kind === mainActionKind) mainStatus = 'success';
                else if (flash.kind === 'error' && mainActionKind === 'copied') mainStatus = 'error';
              }
              const flashedCopy =
                !!flash && flash.id === entry.id && flash.kind === 'copied';
              return (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  isActive={idx === 0}
                  accentClasses={c}
                  activeBadgeLabel={activeBadgeLabel}
                  editing={editingId === entry.id}
                  onStartEdit={() => setEditingId(entry.id)}
                  onCancelEdit={() => setEditingId(null)}
                  onSaveEdit={(text) => {
                    actions.update(entry.id, { text });
                    setEditingId(null);
                  }}
                  onMainAction={() => void onMainAction(entry)}
                  mainActionLabel={mainActionLabel}
                  mainActionFlashLabel={mainActionFlashLabel}
                  mainActionErrorLabel={mainActionErrorLabel}
                  mainActionDisabled={mainActionDisabled}
                  mainActionDisabledReason={mainActionDisabledReason}
                  mainActionFlashed={mainStatus}
                  onCopy={() => void onCopy(entry)}
                  onTogglePin={() => actions.togglePin(entry.id)}
                  onRemove={() => actions.remove(entry.id)}
                  copyFlashed={flashedCopy}
                />
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ===================================================================== */
/* EntryRow                                                               */
/* ===================================================================== */

interface EntryRowProps {
  entry: ClipboardEntry;
  isActive: boolean;
  accentClasses: AccentClasses;
  activeBadgeLabel: string;
  editing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (text: string) => void;
  mainActionLabel: string;
  mainActionFlashLabel: string;
  mainActionErrorLabel: string;
  mainActionDisabled: boolean;
  mainActionDisabledReason: string;
  /** null = idle, 'success' / 'error' = transient feedback (FLASH_MS). */
  mainActionFlashed: MainActionStatus;
  onMainAction: () => void;
  onCopy: () => void;
  onTogglePin: () => void;
  onRemove: () => void;
  copyFlashed: boolean;
}

function EntryRow({
  entry,
  isActive,
  accentClasses,
  activeBadgeLabel,
  editing,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  mainActionLabel,
  mainActionFlashLabel,
  mainActionErrorLabel,
  mainActionDisabled,
  mainActionDisabledReason,
  mainActionFlashed,
  onMainAction,
  onCopy,
  onTogglePin,
  onRemove,
  copyFlashed,
}: EntryRowProps): JSX.Element {
  const [draft, setDraft] = useState(entry.text);
  useEffect(() => {
    if (editing) setDraft(entry.text);
  }, [editing, entry.text]);

  const time = new Date(entry.createdAt);
  const timeLabel = `${pad(time.getHours())}:${pad(time.getMinutes())}`;

  // Card chrome: every entry sits inside an explicit ring-bordered
  // card so the text + action buttons are visually fenced together
  // (used to be a flat `divide-y`, which made it hard to tell which
  // button belonged to which line). The "active" card — i.e. the
  // newest entry, which the user expects to be the device's current
  // system clipboard — gets a heavier ring and a small "当前 ...
  // 剪贴板" header so the user can spot it at a glance.
  const cardClass = clsx(
    'rounded-md bg-bg-card/70 p-2 ring-1 transition-colors',
    isActive
      ? clsx(accentClasses.activeRing, 'ring-2')
      : clsx(accentClasses.cardRing, accentClasses.cardHoverRing),
  );

  if (editing) {
    return (
      <li className={cardClass}>
        <textarea
          className="h-20 w-full resize-none rounded border border-line bg-bg-card p-2 text-[12.5px] leading-relaxed text-ink outline-none focus:border-accent"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              onCancelEdit();
            }
          }}
          autoFocus
        />
        <div className="mt-1 flex items-center gap-1 text-[10px] text-ink-muted">
          <span className="font-mono text-[9px]">{timeLabel}</span>
          <span className="ml-auto" />
          <button
            type="button"
            className="rounded border border-line px-2 py-0.5 text-[10px] text-ink-muted hover:bg-bg-card"
            onClick={onCancelEdit}
          >
            取消
          </button>
          <button
            type="button"
            className="rounded border border-accent/70 bg-accent/15 px-2 py-0.5 text-[10px] text-accent hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => onSaveEdit(draft)}
            disabled={!draft.trim()}
          >
            保存
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className={clsx('group', cardClass)}>
      {isActive && (
        <div
          className={clsx(
            'mb-1 flex items-center gap-1 text-[9px] font-medium uppercase tracking-wider',
            accentClasses.activeText,
          )}
        >
          <span
            className={clsx(
              'inline-block h-1.5 w-1.5 rounded-full',
              accentClasses.activeDot,
            )}
          />
          {activeBadgeLabel}
        </div>
      )}
      {/* Main text — primary visual. 12.5px keeps natural-language
          snippets readable at 4 lines without overflowing the panel. */}
      <p
        className="line-clamp-4 break-words text-[12.5px] leading-relaxed text-ink"
        title={entry.text}
      >
        {entry.text}
      </p>
      {/* Metadata + secondary icons row. The main action used to live
          here too, but tight 380px panels squeezed its label down to a
          vertical 4-character spillage. It's now in its own row at the
          bottom of the card so the label has full width to itself
          without ballooning into a hero button. */}
      <div className="mt-1.5 flex items-center gap-1">
        <span className="font-mono text-[8px] text-ink-muted">
          {timeLabel}
        </span>
        {entry.pinned && (
          <Bookmark
            className="h-3 w-3 text-amber-400"
            aria-label="已置顶"
          />
        )}
        <span className="ml-auto" />
        <button
          type="button"
          className="rounded p-1 text-ink-muted opacity-60 transition group-hover:opacity-100 hover:bg-bg hover:text-ink"
          onClick={onCopy}
          title="复制到本端剪贴板"
        >
          {copyFlashed ? (
            <Check className="h-3.5 w-3.5 text-emerald-400" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
        <button
          type="button"
          className="rounded p-1 text-ink-muted opacity-60 transition group-hover:opacity-100 hover:bg-bg hover:text-ink"
          onClick={onStartEdit}
          title="编辑"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="rounded p-1 text-ink-muted opacity-60 transition group-hover:opacity-100 hover:bg-bg hover:text-ink"
          onClick={onTogglePin}
          title={entry.pinned ? '取消置顶' : '置顶'}
        >
          {entry.pinned ? (
            <Bookmark className="h-3.5 w-3.5 text-amber-400" />
          ) : (
            <BookmarkPlus className="h-3.5 w-3.5" />
          )}
        </button>
        <button
          type="button"
          className="rounded p-1 text-ink-muted opacity-60 transition group-hover:opacity-100 hover:bg-rose-500/20 hover:text-rose-300"
          onClick={onRemove}
          title="删除"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* Main action — its own row at the bottom of the card. Inline-
          flex with auto width so it sits flush-left and stays compact.
          Idle palette comes from the column's accent (sky for PC,
          emerald for device) so the button reads as part of its column
          rather than borrowing the panel's accent (cyan). Success and
          error flashes still use a fixed emerald/rose palette — they
          mean "result", not "category", and the user benefits from
          uniform feedback colors across both columns. */}
      <div className="mt-1.5">
        <button
          type="button"
          className={clsx(
            'inline-flex items-center justify-center rounded px-2 py-1 text-[10.5px] font-medium transition-colors',
            mainActionDisabled
              ? 'cursor-not-allowed border border-line bg-bg/40 text-ink-muted'
              : mainActionFlashed === 'success'
                ? 'border border-emerald-500/50 bg-emerald-500/20 text-emerald-300'
                : mainActionFlashed === 'error'
                  ? 'border border-rose-500/50 bg-rose-500/20 text-rose-300'
                  : accentClasses.mainBtnIdle,
          )}
          onClick={mainActionDisabled ? undefined : onMainAction}
          title={mainActionDisabled ? mainActionDisabledReason : mainActionLabel}
          disabled={mainActionDisabled}
        >
          {mainActionFlashed === 'success'
            ? mainActionFlashLabel
            : mainActionFlashed === 'error'
              ? mainActionErrorLabel
              : mainActionLabel}
        </button>
      </div>
    </li>
  );
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
